import React, { useState, useEffect } from 'react';
import { AuthContext } from './../AuthContext';
import Sidebar from './../Sidebar';
import Footer from './../Footer';
import { site_url } from "./../config.js";
import axios from 'axios';
import { useParams, useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import 'bootstrap/dist/css/bootstrap.min.css';
import { ConnectivityContext } from '../context/ConnectivityContext';
import { cachedGet } from '../services/apiCacheService';


function WorkerEdit() {
  const { user } = React.useContext(AuthContext);
  const { effectiveMode } = React.useContext(ConnectivityContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id; // If id exists, it's edit mode
  
  const [formData, setFormData] = useState({
    id: '',
    w_name: '',
    w_phone: '',
    w_email: '',
    w_address: '',
    w_type: '0', // 0 = Worker, 1 = Admin
    w_password: '',
    w_status: '0' // 1 = Active, 0 = Inactive
  });
  
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [shopCates, setShopCates] = useState([]);
  const [selectedRoutes, setSelectedRoutes] = useState([]);

  // Fetch worker data for edit mode
  useEffect(() => {
    if (isEdit && id) {
      fetchWorkerData();
      fetchWorkerRoutes();
      fetchShopCates();
    }
  }, [id, isEdit]);

  const fetchWorkerData = async () => {
    try {
      const responseData = await cachedGet(site_url + `/worker/details/${id}`, {}, effectiveMode === 'offline');
      const response = { data: responseData };
      if (response.data.status === 'success') {
        const worker = response.data.worker;
        setFormData({
          id: worker.id || '',
          w_name: worker.w_name || '',
          w_phone: worker.w_phone || '',
          w_email: worker.w_email || '',
          w_address: worker.w_address || '',
          w_type: worker.w_type?.toString() || '0',
          w_password: '', // Always empty for security
          w_status: '0'
        });
      }
    } catch (error) {
      console.error('Error fetching worker data:', error);
      Swal.fire('Error', 'Failed to fetch worker data', 'error');
    }
  };

  const fetchWorkerRoutes = async () => {
    try {
      const responseData = await cachedGet(site_url + `/workerroute/${id}`, {}, effectiveMode === 'offline');
      const response = { data: responseData };
      // workerroute contains objects with r_name (route id)
      const routes = (response.data.routes || []).map(r => r.r_name);
      setSelectedRoutes(routes);
    } catch (error) {
      console.error('Error fetching worker routes:', error);
    }
  };

  const fetchShopCates = async () => {
    try {
      const responseData = await cachedGet(site_url + `/shop_cates`, {}, effectiveMode === 'offline');
      const response = { data: responseData };
      setShopCates(response.data.category || response.data.shop_cates || []);
    } catch (error) {
      console.error('Error fetching shop categories:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhoneCheck = async (phone) => {
    if (phone && phone !== formData.w_phone) {
      try {
        const response = await axios.get(site_url + `/worker/check_phone/${phone}`);
        setPhoneError(response.data.exists);
      } catch (error) {
        console.error('Error checking phone:', error);
      }
    } else {
      setPhoneError(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (phoneError) {
      Swal.fire('Error', 'Phone number already exists', 'error');
      return;
    }

    setLoading(true);
    
    try {
      const endpoint = isEdit ? '/worker/update' : '/worker/create';
      const response = await axios.post(site_url + endpoint, formData);

      if (response.data.status === 'success') {
        // Update routes only in edit mode
        if (isEdit) {
          try {
            await axios.post(site_url + '/worker/routes/update', {
              worker_id: formData.id || id,
              route_ids: selectedRoutes,
            });
          } catch (err) {
            console.error('Error updating worker routes:', err);
          }
        }
        Swal.fire('Success', `Worker ${isEdit ? 'updated' : 'created'} successfully`, 'success');
        // navigate('/workers');
      } else {
        Swal.fire('Error', response.data.message || `Failed to ${isEdit ? 'update' : 'create'} worker`, 'error');
      }
    } catch (error) {
      console.error(`Error ${isEdit ? 'updating' : 'creating'} worker:`, error);
      Swal.fire('Error', `Failed to ${isEdit ? 'update' : 'create'} worker`, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div id="appCapsule  p-2" className='container'>
        {/* Header */}
        <div class="section mt-2">
          <div class="section-heading">
            <h2>{isEdit ? 'Edit Worker' : 'Create New Worker'}</h2>
          </div>
        </div>

        {/* Form */}
        <div class="section ">
          <form onSubmit={handleSubmit}>
            <div class="form-row">
              <div class="form-group col-md-6">
                <label htmlFor="w_name">Worker Name</label>
                <input 
                  type="text" 
                  value={formData.w_name} 
                  name="w_name" 
                  class="form-control" 
                  required 
                  placeholder="Worker Name"
                  onChange={handleInputChange}
                />
              </div>
              
              <div class="form-group col-md-6">
                <label htmlFor="w_phone">Phone Number</label>
                <input 
                  value={formData.w_phone} 
                  type="tel" 
                  name="w_phone" 
                  class="form-control" 
                  required 
                  placeholder="Phone Number"
                  onChange={(e) => {
                    handleInputChange(e);
                    handlePhoneCheck(e.target.value);
                  }}
                />
                
                {phoneError && (
                  <div class="alert alert-danger mt-2" role="alert">
                    Phone number already exists
                  </div>
                )}
              </div>

              <div class="form-group col-md-6" style={{display:'none'}}>
                <label htmlFor="w_email">Email Address</label>
                <input 
                  type="email" 
                  value={formData.w_email} 
                  name="w_email" 
                  class="form-control" 
                  placeholder="Email Address"
                  onChange={handleInputChange}
                />
              </div>

              <div class="form-group col-md-6">
                <label htmlFor="w_type">Worker Type</label>
                <select 
                  value={formData.w_type} 
                  name="w_type" 
                  class="form-control"
                  onChange={handleInputChange}
                >
                  <option value="0">Worker</option>
                  <option value="1">Admin</option>
                </select>
              </div>

              <div class="form-group col-md-12">
                <label htmlFor="w_address">Address</label>
                <textarea 
                  value={formData.w_address} 
                  name="w_address" 
                  class="form-control" 
                  rows="3"
                  placeholder="Worker Address"
                  onChange={handleInputChange}
                />
              </div>

              <div class="form-group col-md-6">
                <label htmlFor="w_password">Password</label>
                <input 
                  type="password" 
                  value={formData.w_password} 
                  name="w_password" 
                  class="form-control" 
                  placeholder={isEdit ? "Leave empty to keep current password" : "Password"}
                  onChange={handleInputChange}
                  required={!isEdit}
                />
                {isEdit && (
                  <small class="form-text text-muted">
                    Leave empty to keep current password
                  </small>
                )}
              </div>

              <div class="form-group col-md-6" style={{display:'none'}}>
                <label htmlFor="w_status">Status</label>
                <select 
                  value={formData.w_status} 
                  name="w_status" 
                  class="form-control"
                  onChange={handleInputChange}
                >
                  <option value="0">Active</option>
                  <option value="1">Inactive</option>
                </select>
              </div>
            </div>

            {/* Routes (shop_cates) */}
          
              <div class="form-group col-md-12">
                <label>Assign Routes</label>
                <div class="row">
                  {shopCates.map((cate) => (
                    <div key={cate.id} class="col-12">
                      <div class="form-check">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          id={`route_${cate.id}`}
                          checked={selectedRoutes.includes(cate.id)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setSelectedRoutes((prev) => {
                              if (checked) return Array.from(new Set([...prev, cate.id]));
                              return prev.filter((rid) => rid !== cate.id);
                            });
                          }}
                        />
                        <label class="form-check-label" htmlFor={`route_${cate.id}`}>
                          {cate.ename || cate.name}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>


            {/* Submit Buttons */}
            <div class="form-group mt-4">
              <div class="row" style={{width:'100%'}}>
                <div class="col-6">
                  <button 
                    type="button" 
                    class="btn btn-secondary w-100"
                    onClick={() => navigate('/workers')}
                  >
                    Cancel
                  </button>
                </div>
                <div class="col-6">
                  <button 
                    type="submit" 
                    class="btn btn-primary w-100"
                    disabled={loading || phoneError}
                  >
                    {loading ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update Worker' : 'Create Worker')}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      
    </>
  );
}

export default WorkerEdit;
